import clsx from 'clsx';
import { motion } from 'framer-motion';
import React, { useRef, useState } from 'react';

import { useIsTabbing, useResizeObserver } from '../../hooks';
import { CarouselProps } from './Carousel.types';
import { CarouselControls } from './CarouselControls';

export function Carousel({
  children,
  className,
  scrollIncrement = 1,
  withControls = true,
  isOrderedList,
  testid = 'carousel',
}: CarouselProps): JSX.Element {
  const [currentIndex, setCurrentIndex] = useState(null);
  const [rootWidth, setRootWidth] = useState(0);
  const [cardsContainerWidth, setCardsContainerWidth] = useState(0);
  const [cancelNextClickEvent, setCancelNextClickEvent] = useState(false);

  const isTabbing = useIsTabbing();

  const cardMargin = 32;
  const stepSize =
    (cardsContainerWidth + cardMargin) / React.Children.count(children);
  const dragConstraintX = rootWidth - cardsContainerWidth;

  // Current X Position accounting for max width of the container
  const getCurrentX = () => {
    // Uninitialized or the container is wider than the cards
    if (!rootWidth || !cardsContainerWidth || rootWidth > cardsContainerWidth) {
      return 0;
    }
    // X at beginning of current card
    if (currentIndex * stepSize < cardsContainerWidth - rootWidth) {
      return currentIndex * stepSize;
    }
    // At the end
    return cardsContainerWidth - rootWidth;
  };
  const currentX = getCurrentX();
  const atEnd = Boolean(currentX && currentX + dragConstraintX === 0);

  /**
   * Refs for DOM nodes
   */
  const nodes = {
    root: useRef<HTMLDivElement>(null), // carousel
    cards: useRef<HTMLUListElement>(null), // carousel__cards
    card: [useRef<HTMLLIElement>(null)], // carousel__card
  };

  /**
   * Resize Observer for measuring Root Width and Cards Container Width
   */
  useResizeObserver(nodes.root, () =>
    setRootWidth(nodes.root.current?.getBoundingClientRect().width)
  );
  useResizeObserver(nodes.cards, () =>
    setCardsContainerWidth(nodes.cards.current?.getBoundingClientRect().width)
  );

  /**
   * Handle Carousel Controls
   */
  const handleBack = () => {
    const nextIndex =
      currentIndex - scrollIncrement > 0 ? currentIndex - scrollIncrement : 0;
    setCurrentIndex(nextIndex);
  };

  const handleNext = () => {
    const nextIndex =
      currentIndex + scrollIncrement < React.Children.count(children) - 1
        ? currentIndex + scrollIncrement
        : React.Children.count(children) - 1;
    setCurrentIndex(nextIndex);
  };

  /**
   * Handle drag release to snap to card
   */
  const handleDragEnd = (offsetX: number) => {
    // Forward: 0 | Reverse: -1
    const direction = offsetX < 0 ? 0 : -1;
    // Prevent overscrolling
    if (direction === 0 && atEnd) return;
    // Raw X position (not snapped to card)
    const rawXPosition = currentIndex * stepSize - offsetX;
    // Find first card index greater than rawXPosition
    const cardIndex = React.Children.toArray(children).findIndex(
      (_, i) => i * stepSize > rawXPosition
    );
    // Prevent edge case where card is undefined
    if (!cardIndex) return;

    // Next card index
    //   accounting for drag direction
    //   and that index is non-negative
    const nextIndex = cardIndex + direction >= 0 ? cardIndex + direction : 0;
    setCurrentIndex(nextIndex);
  };

  const Tag = isOrderedList ? motion.ol : motion.ul;

  return (
    <div
      ref={nodes.root}
      className={clsx('carousel', className)}
      data-testid={testid}
    >
      <Tag
        ref={nodes.cards}
        className='carousel__cards'
        onMouseDown={(event) => {
          event.preventDefault();
          setCancelNextClickEvent(false);
        }}
        onClick={(e) => {
          cancelNextClickEvent && e.preventDefault();
        }}
        animate={{
          x: -currentX,
        }}
        drag='x'
        dragConstraints={{
          left: dragConstraintX,
          right: 0,
        }}
        dragElastic={false}
        transition={{
          ease: 'easeOut',
          duration: 0.4,
        }}
        onDragStart={() => setCancelNextClickEvent(true)}
        onDragEnd={(_, info) => handleDragEnd(info.offset.x)}
        dragMomentum={false}
      >
        {React.Children.map(children, (child, cardIndex) =>
          React.cloneElement(child, {
            ...child.props,
            key: cardIndex,
            onFocus: isTabbing ? () => setCurrentIndex(cardIndex) : undefined,
            isFocused: isTabbing && cardIndex === currentIndex,
            forwardedRef: nodes.card[cardIndex],
          })
        )}
      </Tag>
      {withControls && (
        <CarouselControls
          currentIndex={currentIndex}
          handleBack={handleBack}
          handleNext={handleNext}
          numCards={React.Children.count(children)}
          atEnd={atEnd}
        />
      )}
    </div>
  );
}
